import StreamRequest from './StreamRequest';

interface TTSRequestArgs {
  clipId: string;
  projectId: string;
  text: string;
  textFormat?: string;
  actor: {
    id: number;
    variantId: number;
    initials: string;
    version: string;
  };
  isPreview?: boolean;
}

interface TTSRequestEvents {
  sent: {};
  headersReceived: {
    id: string;
    createdAt: Date;
    name: string;
    fileName: string;
    index: number;
    source: string;
    src: string;
    sequence: string;
  };
  complete: {};
  error: {
    message: string;
    debugContext?: Record<string, unknown>;
  };
}

interface TTSRequestEventsMap {
  headersReceived: CustomEvent<TTSRequestEvents['headersReceived']>;
  complete: CustomEvent<TTSRequestEvents['complete']>;
  error: CustomEvent<TTSRequestEvents['error']>;
}

const TTSRequestErrorMessages = {
  onHeadersReceivedInvalidStatus: 'Request failed with status ',
  parsingFailedJsonResponse: 'Request failed with unknown error message',
  networkError: 'Please check your network connection and retry',
  genericError: 'Request failed',
  timeoutError: 'Request timeout, please try again',
};

/**
 * Abstraction on top of `StreamRequest` to make interacting with a TTS request easier. Handles
 * response errors and adds easy to consume events.
 *
 * @class TTSRequest
 *
 * @emits headersReceived [TTSRequestEvents.headersReceived]
 * @emits error [TTSRequestEvents.error]
 * @emits complete [TTSRequestEvents.complete]
 */
export default class TTSRequest extends EventTarget {
  public static readonly PreviewEndpoint: string = `/api/text_to_speech/preview/clip`;

  public static readonly TTSEndpoint: string = `/api/v1/text_to_speech/stream`;

  public status:
    | 'pending'
    | 'inflight'
    | 'headersReceived'
    | 'complete'
    | 'failed' = 'pending';

  public readonly id!: string;

  public readonly projectId!: string;

  public actor: TTSRequestArgs['actor'];

  public text: string;

  public textFormat: string | undefined;

  private streamRequest: StreamRequest;

  // Used for timing the request
  private timingStart: number = -1;

  private timingHeadersReceived: number = -1;

  private timingRequestComplete: number = -1;

  private isPreview: boolean = false;

  constructor({
    actor,
    text,
    projectId,
    clipId,
    textFormat,
    isPreview = false,
  }: TTSRequestArgs) {
    super();
    this.id = clipId;
    this.projectId = projectId;
    this.actor = actor;
    this.text = text;
    this.textFormat = textFormat;
    this.streamRequest = new StreamRequest();
    this.isPreview = isPreview;
  }

  public get stream(): StreamRequest {
    return this.streamRequest;
  }

  public get timing() {
    return {
      headersReceived: this.timingHeadersReceived,
      requestComplete: this.timingRequestComplete,
    };
  }

  public start() {
    if (this.status !== 'pending') {
      throw new Error(
        `TTSRequest::start invariant failed, request already started`
      );
    }
    // Setup request event listeners
    this.streamRequest.addEventListener(
      'headersReceived',
      this.onHeadersReceived
    );
    this.streamRequest.addEventListener('load', this.onLoad);
    this.streamRequest.addEventListener('abort', this.onError);
    this.streamRequest.addEventListener('error', this.onError);

    // Begin the request
    const requestParams: Record<string, string> = {
      id: this.id,
      projectId: this.projectId,
      text: this.text,
      speaker_id: `${this.actor.id}`,
      speaker_variant_id: `${this.actor.variantId}`,
      version: this.actor.version,
    };
    // Add formatted text to api request if it exists
    if (this.textFormat) {
      requestParams.textFormat = this.textFormat;
    }
    this.status = 'inflight';
    this.timingStart = Date.now();
    const endpoint = this.isPreview
      ? TTSRequest.PreviewEndpoint
      : TTSRequest.TTSEndpoint;
    this.streamRequest.post(endpoint, requestParams);
  }

  private onHeadersReceived = () => {
    this.status = 'headersReceived';
    this.timingHeadersReceived = Date.now() - this.timingStart;
    if (this.streamRequest.status === 200) {
      this.dispatchEvent(
        new CustomEvent<TTSRequestEvents['headersReceived']>(
          'headersReceived',
          {
            detail: {
              id: this.streamRequest.getResponseHeader('X-Clip-ID') as string,
              createdAt: new Date(
                this.streamRequest.getResponseHeader(
                  'X-Clip-Created-At'
                ) as string
              ) as Date,
              name: this.streamRequest.getResponseHeader(
                'X-Clip-Name'
              ) as string,
              fileName: this.streamRequest.getResponseHeader(
                'X-Clip-File-Name'
              ) as string,
              index: parseInt(
                this.streamRequest.getResponseHeader('X-Clip-Index') as string,
                10
              ),
              src: this.streamRequest.getResponseHeader('X-Clip-URL') as string,
              sequence: this.streamRequest.getResponseHeader(
                `X-Clip-Sequence`
              ) as string,
              source: this.streamRequest.getResponseHeader(
                'X-Clip-Source'
              ) as string,
            },
          }
        )
      );
    }
    // NOTE: currently a failed render will still trigger to `load` event with non-200 status code,
    // so we leave the error handling up to that event.
  };

  private onLoad = async () => {
    if (
      this.streamRequest.status !== 200 &&
      this.streamRequest.response?.headers
        ?.get('content-type')
        ?.includes('application/json')
    ) {
      // Stream failed, but returned a JSON response with reasoning
      const fileReader = new FileReader();
      if (this.streamRequest.response) {
        const blob = await this.streamRequest.response.blob();
        fileReader.readAsText(blob);
      }
      fileReader.onload = () => {
        try {
          if (typeof fileReader.result === 'string') {
            const errorMessage = JSON.parse(fileReader.result).message;
            this.dispatchError(errorMessage);
          } else {
            throw new Error(`invalid fileReader result format`);
          }
        } catch (error) {
          this.dispatchError(TTSRequestErrorMessages.parsingFailedJsonResponse);
        }
      };
      fileReader.onerror = () => {
        this.dispatchError(TTSRequestErrorMessages.parsingFailedJsonResponse);
      };
      this.status = 'failed';
    } else if (this.streamRequest.status !== 200) {
      // Stream failed with no JSON response
      this.status = 'failed';
      this.dispatchError(
        `${TTSRequestErrorMessages.onHeadersReceivedInvalidStatus}${this.streamRequest.status}`
      );
    } else {
      // Stream completed successfully
      this.status = 'complete';
      this.timingRequestComplete = Date.now() - this.timingStart;
      this.dispatchEvent(
        new CustomEvent<TTSRequestEvents['complete']>('complete')
      );
    }
  };

  private onError = (event: CustomEvent | Event) => {
    const debugContext =
      event instanceof CustomEvent
        ? (event.detail?.debugContext ?? event.detail)
        : undefined;

    if (this.status === 'inflight') {
      this.dispatchError(TTSRequestErrorMessages.networkError, debugContext);
    } else if (event instanceof CustomEvent && event.detail?.message) {
      this.dispatchError(event.detail.message, debugContext);
    } else {
      this.dispatchError(TTSRequestErrorMessages.genericError, debugContext);
    }
    this.status = 'failed';
  };

  private onTimeout = () => {
    this.dispatchError(TTSRequestErrorMessages.timeoutError);
    this.status = 'failed';
  };

  private dispatchError = (
    message: string,
    debugContext?: Record<string, unknown>
  ) => {
    this.dispatchEvent(
      new CustomEvent<TTSRequestEvents['error']>('error', {
        detail: {
          message,
          ...(debugContext && { debugContext }),
        },
      })
    );
  };

  /**
   * Helper method required for type checking our event listeners.
   */
  public on<K extends keyof TTSRequestEventsMap>(
    type: K,
    listener: (ev: TTSRequestEventsMap[K]) => any
  ) {
    this.addEventListener(type, listener as EventListener);
  }

  public getObjectURL(): string | undefined {
    return this.streamRequest.getObjectURL();
  }
}
